<script lang="ts">import { onMount } from 'svelte';
import { prefersReducedMotion } from 'svelte/motion';
import { browser } from '$app/environment';
import { transitionDuration } from '../../../helpers/duration';
import AccordionToggle from '../AccordionToggle/AccordionToggle.svelte';
const { size = 'regular', open = false, disabledSelfClose = false, animationDuration = transitionDuration('default'), 
// Defaults to var(--easing-standard);
animationTimingFunction = 'cubic-bezier(0.4, 0, 0.2, 1)', onClick = () => { }, onToggle = () => { }, summary, content, class: className = '', ...rest } = $props();
let detailsElement;
let contentElement;
// Variable to keep track if open has changed from the parent element
let lastOpen = $state(open);
let isToggled = $state(open);
let isAnimating = false;
let animation = null;
// This is needed for when the parent component controls the open prop. The open prop cannot be used directly on the
// details element because of this, so setting the attribute onMount without an animation handles it
onMount(() => {
    if (open) {
        detailsElement.open = true;
        isToggled = true;
        lastOpen = true;
    }
});
// This is logic for when the parent component controls the open prop and changes it from true to false or false to
// true. There should still be animations even if the parent component is controlling its open state
$effect(() => {
    if (detailsElement) {
        const currentOpen = open;
        // If the lastOpen variable is not the same as the currentOpen value that is tracked in the component instance,
        // then we know the parent element is controlling it. isToggled needs to be set so the AccordionToggle displays
        // correctly
        if (lastOpen !== currentOpen) {
            if (currentOpen) {
                isToggled = true;
                detailsElement.open = true;
            }
            else {
                if (detailsElement.open) {
                    if (prefersReducedMotion.current) {
                        contentElement.style.opacity = '1';
                        detailsElement.open = false;
                        isToggled = false;
                    }
                    else {
                        animateAccordion(false);
                    }
                }
                else {
                    isToggled = false;
                }
            }
            // Variable to keep track of the last known open value, so toggleAccordion is not called again when it is not
            // needed
            lastOpen = currentOpen;
        }
    }
});
function toggleAccordion(event) {
    event?.preventDefault();
    if (isAnimating) {
        return;
    }
    // Variable to keep track if the details element has the open attribute or not. The open attribute does not get
    // added or removed until the animation is finished in the onAnimateAccordionFinish function
    const isOpening = !detailsElement.open;
    // There are instances where there should always be an item open in the accordion because it has another action like
    // displaying an image on a different part of the page. This prevents all items from being closed
    if (disabledSelfClose && !isOpening && isToggled) {
        return;
    }
    // Toggle without the animation if the user prefers reduced motion
    if (prefersReducedMotion.current) {
        // If for some reason a visitor has reduce motion turned off but turns it on after loading the page, then the
        // inline styling from animateAccordion needs to be overridden
        contentElement.style.opacity = '1';
        detailsElement.open = isOpening;
        isToggled = isOpening;
        // An event is checked because a callback function should only be called if it is being used as an uncontrolled
        // component. In a controlled component the parent element will already know the state of isOpen
        if (isOpening || event) {
            onToggle?.({ isOpen: isOpening });
        }
        if (isOpening) {
            // onClick is only called if the accordion is opening because this is mostly used for tracking events and the
            // event should not be sent again if the accordion is closing
            onClick?.();
        }
        return;
    }
    animateAccordion(isOpening);
}
function animateAccordion(isOpening) {
    isAnimating = true;
    const startHeight = detailsElement.offsetHeight;
    // Set the opacity to 0 before setting detailsElement.open so there is no chance the user will see a flash of
    // content, but the transition also has to be removed because it was fading out slowly. This is done here because
    // off the possiblities for the element to be open by default where there should not be a delay in showing the
    // content, but also the transition-delay of fading in newly opened content where it cannot just be done in CSS
    contentElement.style.opacity = '0';
    contentElement.style.transition = 'none';
    if (isOpening) {
        // Safari doesn't update offsetHeight until open is set, so startHeight and endHeight were being calculated as
        // the same number. If startHeight and endHeight are the same, then no animation happens
        detailsElement.open = true;
    }
    // Lock the height temporarily during the animation. This is reset in the onAnimationFinish function
    detailsElement.style.height = `${startHeight}px`;
    const endHeight = isOpening ? detailsElement.scrollHeight : detailsElement.firstElementChild?.scrollHeight || 0;
    // Cancel the current animation if one is already running
    if (animation) {
        animation.cancel();
    }
    animation = detailsElement.animate({
        height: [`${startHeight}px`, `${endHeight}px`],
    }, {
        duration: animationDuration,
        easing: animationTimingFunction,
    });
    // Remove the inline transition, so the CSS transition is used
    contentElement.style.transition = '';
    if (isOpening) {
        requestAnimationFrame(() => {
            isToggled = true;
            // Slightly delay adding opacity to the content so the transition can be seen
            setTimeout(() => (contentElement.style.opacity = '1'), transitionDuration('quick'));
            onToggle?.({ isOpen: true });
            // onClick is only called if the accordion is opening because this is mostly used for tracking events and the
            // event should not be sent again if the accordion is closing
            onClick?.();
        });
    }
    else {
        isToggled = false;
        onToggle?.({ isOpen: false });
    }
    animation.onfinish = () => onAnimateAccordionFinish(isOpening);
    animation.oncancel = () => {
        isAnimating = false;
    };
}
function onAnimateAccordionFinish(isOpen) {
    detailsElement.open = isOpen;
    detailsElement.style.height = '';
    // Reset the variables back to their initial values
    isAnimating = false;
    animation = null;
}
</script>

<details
  {...rest}
  class="AccordionItem AccordionItem--{size} {className}"
  open={browser ? undefined : open}
  bind:this={detailsElement}
>
  <summary class="AccordionItem__summary" onclick={toggleAccordion}>
    <div class="AccordionItem__heading">
      {@render summary?.()}
      <AccordionToggle {isToggled} {size} />
    </div>
  </summary>
  <div class="AccordionItem__content" bind:this={contentElement}>
    {@render content?.()}
  </div>
</details>

<style>@layer design-system-components.pwds {
  .AccordionItem {
    width: 100%;
    border-top: var(--border-line-width) var(--border-line-style) var(--color-neutral-700);
    overflow-y: clip;
  }
  .AccordionItem, .AccordionItem.AccordionItem--regular {
    --accordion-item-summary-padding: var(--accordion-item-size-regular-summary-padding);
  }
  .AccordionItem.AccordionItem--small {
    --accordion-item-summary-padding: var(--space-30);
  }
  .AccordionItem__summary {
    padding-top: var(--accordion-item-summary-padding);
    padding-bottom: var(--accordion-item-summary-padding);
    cursor: pointer;
    list-style: none;
  }
  .AccordionItem__summary::-webkit-details-marker {
    display: none;
  }
  .AccordionItem__summary:focus-visible {
    outline: none;
  }
  .AccordionItem__summary:focus-visible .AccordionItem__heading {
    outline: var(--accessibility-focus-outline-width) var(--accessibility-focus-outline-style, solid) var(--accessibility-focus-outline-color);
    border-radius: var(--accessibility-focus-outline-border-radius);
    outline-offset: var(--accessibility-focus-outline-offset);
  }
  .AccordionItem__heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-20);
  }
  .AccordionItem__content {
    will-change: opacity;
    transition: opacity var(--duration-quick) var(--easing-decelerate);
  }
  @media (prefers-reduced-motion: reduce) {
    .AccordionItem__content {
      transition: none;
    }
  }
}</style>
